import React from "react";
import { Button } from "@/components/ui/button";
import { ShoppingBag, Check, Store, X as XIcon } from "lucide-react";

/**
 * Shopping Mode — mobile-first full-screen view triggered from the
 * Grocery page when the user is actually at the shop. Big tap targets,
 * grouped by category, one-tap check-off (which moves the item to the
 * pantry).
 *
 * Purely presentational: parent supplies items, vendors, category order
 * and callbacks.
 */
export default function ShopMode({
  items,
  vendors,
  categories,
  onExit,
  onCheckOff,
}) {
  const upcoming = items.filter((i) => i.status === "upcoming");
  const grouped = categories
    .map((c) => ({ cat: c, list: upcoming.filter((i) => i.category === c) }))
    .filter((g) => g.list.length > 0);
  const remaining = upcoming.length;
  const vendorTagged = upcoming.filter((i) => i.vendor_id).length;

  return (
    <div className="-m-6 md:-m-10 lg:-m-12 min-h-screen bg-hearth-paper" data-testid="shop-mode">
      <header className="sticky top-0 z-10 glass border-b border-hearth-line">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center gap-3">
          <ShoppingBag size={22} className="text-hearth-clay" />
          <div className="flex-1">
            <div className="font-display text-2xl text-hearth-ink leading-none">Shopping mode</div>
            <div className="caption mt-1">
              <span className="num text-hearth-ink">{remaining}</span> left
              {vendorTagged > 0 && <> · <span className="num text-hearth-ink">{vendorTagged}</span> with vendor</>}
            </div>
          </div>
          <Button
            data-testid="shop-exit"
            onClick={onExit}
            variant="ghost"
            className="rounded-full"
          >
            <XIcon size={16} className="mr-1" /> Exit
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-6 pb-32">
        {grouped.length === 0 ? (
          <div className="text-center py-20">
            <div className="mx-auto w-16 h-16 rounded-full bg-hearth-sage/15 text-hearth-sageDark grid place-items-center mb-4">
              <Check size={28} />
            </div>
            <h2 className="font-display text-3xl text-hearth-ink">All done.</h2>
            <p className="text-hearth-mute mt-2">Everything on the list has been ticked off.</p>
            <Button
              onClick={onExit}
              className="rounded-full mt-6 bg-hearth-clay hover:bg-hearth-clayDark text-white px-6"
            >
              Back to list
            </Button>
          </div>
        ) : (
          grouped.map((g) => (
            <section key={g.cat} className="mb-7">
              <h2 className="font-display text-2xl text-hearth-ink mb-2 flex items-baseline justify-between">
                <span>{g.cat}</span>
                <span className="caption num">{g.list.length}</span>
              </h2>
              <div className="space-y-2">
                {g.list.map((it) => {
                  const vname = vendors.find((v) => v.id === it.vendor_id)?.name;
                  return (
                    <button
                      key={it.id}
                      data-testid={`shop-row-${it.id}`}
                      onClick={() => onCheckOff(it.id)}
                      className="w-full flex items-center gap-4 p-4 warm-card hover:bg-hearth-stone/40 active:bg-hearth-sage/15 transition text-left"
                    >
                      <span className="w-10 h-10 rounded-full border-2 border-hearth-line shrink-0 grid place-items-center group-active:border-hearth-sage">
                        <Check size={20} className="opacity-0" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-lg text-hearth-ink font-medium leading-tight truncate">
                          {it.name}
                          {it.quantity && (
                            <span className="text-hearth-mute font-normal text-base">
                              {" "}· {it.quantity}
                              {it.unit ? ` ${it.unit}` : ""}
                            </span>
                          )}
                        </div>
                        {(it.note || vname) && (
                          <div className="text-xs text-hearth-mute mt-1 flex items-center gap-2 truncate">
                            {it.note && <span>{it.note}</span>}
                            {vname && (
                              <span className="inline-flex items-center gap-1 text-hearth-clayDark">
                                <Store size={11} /> {vname}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </main>
    </div>
  );
}
